import { Grid, ResourcesListContainer } from '../styles';
import React, { useRef } from 'react';
import { Content } from '../content';
import { ResourceRowsSkeleton, Skeleton } from 'components/skeleton';
import { useSelector } from 'react-redux';
import { selectResources } from 'store/resources/selectors';
import { useAppInstanceConfigFeature } from 'hooks/use-app-instance-config-feature.hook';
import { useIsMapViewEnabled } from 'hooks/use-is-map-view-enabled.hook';
import { FloorPlanFooter } from 'components/floor-plan-footer';
import { useFloorplanViewState } from 'floorplan/pages/native-floorplan/hooks/use-floorplan-view-state.hook';

interface ResourcesListProps {
  isLoadingState: boolean;
  shouldShowFloorPlanFooter: boolean;
}

export const ResourcesList = ({ isLoadingState, shouldShowFloorPlanFooter }: ResourcesListProps): JSX.Element => {
  const resources = useSelector(selectResources);
  const { isCompactUiEnabled } = useAppInstanceConfigFeature();
  const isMapViewEnabled = useIsMapViewEnabled();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Read once for the whole list: the rows read it themselves for their own shape (see
  // `ResourceItem`), but the spacing between them belongs to the container. Not gated on floorplan
  // availability: the redesigned list ships wherever the flag is on, toggle or not.
  const { isRedesignedListEnabled } = useFloorplanViewState();

  // Under the same gate as the rest of the redesign: a skeleton has to be the shape of what it is
  // waiting for, and the row cards load into nothing like the photo tiles.
  const skeleton = isRedesignedListEnabled ? (
    <ResourceRowsSkeleton />
  ) : (
    <Skeleton isCompactUiEnabled={isCompactUiEnabled} />
  );

  return (
    <ResourcesListContainer isLoadingState={isLoadingState} isMapViewEnabled={isMapViewEnabled}>
      {isLoadingState ? (
        skeleton
      ) : (
        <Grid
          id="scrollable-resources-list"
          ref={scrollContainerRef}
          isNoResources={resources?.length === 0 && !isLoadingState}
          isCompactUiEnabled={isCompactUiEnabled}
          isRowList={isRedesignedListEnabled}
        >
          <Content scrollContainerRef={scrollContainerRef} resources={resources} />
        </Grid>
      )}
      {shouldShowFloorPlanFooter && <FloorPlanFooter buttonTextId="floorPlan" isMapViewEnabled={isMapViewEnabled} />}
    </ResourcesListContainer>
  );
};
